import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from "@angular/router";
import { NgForOf, NgIf } from "@angular/common";
import {
  ClrCommonFormsModule,
  ClrDatagridModule,
  ClrDatagridSortOrder,
  ClrIconModule,
  ClrModalModule,
  ClrTooltipModule
} from "@clr/angular";
import {
  ClarityIcons,
  floppyIcon,
  pencilIcon,
  timesIcon,
  twoWayArrowsIcon,
  infoCircleIcon,
  pdfFileIcon
} from "@cds/core/icon";
import { AlertService, AuthService, FileStoreService, IAlertType, MaterialStoreService } from "../../../_shared/services";
import { Files, MaterialModel, MaterialReadResponse } from "../../../_shared/interfaces";
import { EditMaterialComponent } from "./edit-material/edit-material.component";
import { NgxExtendedPdfViewerModule } from "ngx-extended-pdf-viewer";
import { filesBaseUrl } from "../../../_shared/utils/rest-url";

@Component({
  selector: 'app-material',
  standalone: true,
  imports: [
    RouterLink,
    ClrIconModule,
    ClrCommonFormsModule,
    ReactiveFormsModule,
    ClrDatagridModule,
    ClrModalModule,
    ClrTooltipModule,
    NgIf,
    NgForOf,
    EditMaterialComponent,
    NgxExtendedPdfViewerModule
  ],
  templateUrl: './material.component.html',
  styleUrl: './material.component.sass'
})
export class MaterialComponent implements OnInit {
  public materialForm!: FormGroup;
  materials: MaterialModel[] = [];
  loading: boolean = true;
  descSort: ClrDatagridSortOrder = ClrDatagridSortOrder.ASC;
  modalContent: string = '';
  modalUrl: string = '';
  modalSize: string = 'lg';
  showPDF: boolean = false;
  private datenblattFilesById: Record<string, Files> = {};

  @Input() editMaterial: number = 0; // 0 = Anzeige; 1 = Bearbeitung, 2 = Editieren
  @Input() editMaterialId: number = 0;
  @Input() updatingMaterial: boolean = false;

  constructor(
    private materialStore: MaterialStoreService,
    private alertDispatcher: AlertService,
    private fb: FormBuilder,
    private authService: AuthService,
    private fileStore: FileStoreService
  ) {
    ClarityIcons.addIcons(twoWayArrowsIcon, pencilIcon, floppyIcon, timesIcon, infoCircleIcon, pdfFileIcon);
    this.materialForm = this.fb.group({});
  }

  ngOnInit(): void {
    this.loading = true;
    this.editMaterial = 0;
    this.updatingMaterial = false;
    this.loadMaterials();
    this.initForm();
  }

  initForm() {
    this.materialForm = this.fb.group({
      material_id: [0],
      material: [''],
      werkstoffbezeichnung: [''],
      werkstoffnummer: [''],
      waermebehandlungszustand: [''],
      description: [''],
      alternative: [0],
      datenblatt: [''],
    });
  }

  loadMaterials() {
    this.materialStore.getMaterials().subscribe({
      next: (data: MaterialReadResponse) => {
        if (!data || !data.data || data.data.length === 0) {
          this.alertDispatcher.show({
            message: ' Keine Materialien gefunden! ',
            clrAlertType: IAlertType.WARNING
          }, 5000);
          this.loading = false;
          this.materials = [];
          return;
        }
        this.materials = data.data;
      },
      complete: () => {
        this.loading = false;
        this.loadDatenblattFiles();
      },
      error: () => {}
    });
  }

  private normalizePayload(payload: any) {
    return {
      ...payload,
      material_id: Number(payload?.material_id ?? 0),
      alternative: payload?.alternative === '' || payload?.alternative === null
        ? null
        : Number(payload?.alternative ?? 0),
    };
  }

  addMaterial() {
    const payload = this.normalizePayload(this.materialForm.value);
    this.materialStore.addMaterial(payload).subscribe({
      next: () => {
        this.loadMaterials();
        this.initForm();
      },
      error: () => {}
    });
  }

  updateMaterialMode(materialId: number) {
    this.editMaterial = 2;
    this.editMaterialId = materialId;
    this.updatingMaterial = true;
  }

  onUpdateMaterial(_done: boolean) {
    this.editMaterial = 1;
    this.updatingMaterial = false;
    this.editMaterialId = 0;
    this.loadMaterials();
  }

  deleteMaterial(materialId: number, material: string) {
    if (confirm('Material "' + material + '" wirklich löschen?')) {
      this.materialStore.deleteMaterial(materialId).subscribe({
        next: () => this.loadMaterials(),
        error: () => {}
      });
    }
  }

  getDatenblattIds(value?: string | null): string[] {
    const raw = String(value ?? '');
    return raw
      .split(/[\s,;]+/)
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
  }

  getDatenblattFiles(item: MaterialModel): Files[] {
    const ids = this.getDatenblattIds(item.datenblatt);
    return ids
      .map((id) => this.datenblattFilesById[id])
      .filter((file): file is Files => !!file);
  }

  private loadDatenblattFiles() {
    const ids = this.materials
      .flatMap((item) => this.getDatenblattIds(item.datenblatt));
    const uniqueIds = Array.from(new Set(ids));
    if (uniqueIds.length === 0) {
      this.datenblattFilesById = {};
      return;
    }
    this.fileStore.getFilesByIds(uniqueIds).subscribe({
      next: (res) => {
        const files = res?.object ?? [];
        const map: Record<string, Files> = {};
        for (const file of files) {
          if (file?.id) {
            map[file.id] = file;
          }
        }
        this.datenblattFilesById = map;
      },
      error: () => {
        this.datenblattFilesById = {};
      }
    });
  }

  getDownloadUrl(fileId: string): string {
    const token = this.authService.getAccessToken();
    const base = `${filesBaseUrl()}/download_file`;
    return token
      ? `${base}?file_id=${encodeURIComponent(fileId)}&token=${encodeURIComponent(token)}`
      : `${base}?file_id=${encodeURIComponent(fileId)}`;
  }

  openPdfFile(file: Files, material?: MaterialModel) {
    if (!file?.id) {
      return;
    }
    this.modalUrl = this.getDownloadUrl(file.id);
    this.modalContent = material?.material || file.fileName || 'Datenblatt';
    this.modalSize = 'lg';
    this.showPDF = true;
  }

}
